/**
 * Review v0 (Review Flow v2.0, Step 1 — YAR-551): turns every pending review
 * item of the household into the one list the Review screen renders.
 *
 * Pure — no React, no clock, no Supabase — so every card state is a unit
 * test. The page (C4) feeds it the rows and proposal targets read by the
 * review service (A7).
 *
 * Rules it encodes:
 * - A card's kind comes from detection only (`resolveProposal`): a verified
 *   update is a `change`, a verified cancel is `cancelled`, anything else —
 *   no hint, target gone, target unreadable — is `new` (YAR-551 edge case,
 *   YAR-493 AC7).
 * - `buildReviewList` is one pass, in this order:
 *   1. Cards. A task whose related update item is not a card on the list
 *      (set aside, committed, on a closed email) carries the badge of that
 *      item's event as read (F-7), with no item to drop it.
 *   2. Fold (F-1). A new task folds under its event card when that card is
 *      new or a change. One whose event card is a cancellation is its own
 *      card with no badge (confirming deletes that event; the task is
 *      inserted unlinked).
 *   3. Silence (F-8, F-9). A change card that changes nothing (`diffProposal`
 *      empty) but has folded tasks is not listed: its tasks become top-level
 *      cards badged with it, and Confirm all sends it with them
 *      (`silentAnchorId`, `confirmItemIds`). Decided on the data as read, so
 *      a local hide never makes the anchor flash back.
 *   4. Hide (C5). Local removals leave at once: a new event with its tasks;
 *      a change card alone, its tasks coming out badged with it.
 *   5. Order (D1, F-5). One list, no section headings: changes and
 *      cancellations first, in read order, then new items by `created_at`
 *      (ties by read position).
 * - A cancelled event lists its open schedule tasks (`cancelTasks`), the set
 *   apply_review_email cancels with it. They are schedule rows, shown for
 *   information: never counted, gated or sent by Confirm all.
 * - The Confirm all gate is today's (`reviewConfirmGate`, EmailReviewModal):
 *   undated events block, then undated tasks ask. Change and cancel cards
 *   are exempt — confirming them never needs a date (approve_email leaves an
 *   unmentioned date untouched, and a cancel takes the target's).
 */
import { formatLongDateRange, formatTime, formatTimeRange } from '@/lib/date';
import {
  diffProposal,
  resolveProposal,
  type ProposalChange,
} from '@/lib/reviewProposal';
import { senderDisplayName } from '@/lib/sender';
import type { UploadKind } from '@/lib/uploadKind';
import type { ProposalTarget } from '@/services/email.service';
import type { HouseholdReviewItem } from '@/services/review.service';
import type { Child, ReviewItem } from '@/types';
import { REVIEW_COPY } from './reviewCopy';

/**
 * What the list needs of a row: the review item plus its email's subject
 * and sender — the review service's `HouseholdReviewItem` (A7), as read.
 */
export type ReviewListSourceItem = HouseholdReviewItem;

export type ReviewItemKind = 'new' | 'change' | 'cancelled';

export interface ReviewListItem {
  id: string;
  type: 'task' | 'event';
  kind: ReviewItemKind;
  title: string;
  /** null on an undated item → the Add date tag on an event. */
  whenLabel: string | null;
  undatedEvent: boolean;
  undatedTask: boolean;
  placeLabel: string | null;
  /**
   * What the details sheet's Location row shows: a change's target location
   * as it is today; otherwise `placeLabel`.
   */
  detailLocation: string | null;
  /** Change cards only (`diffProposal`). */
  changes: ProposalChange[];
  /** Cancelled cards: `cancelNoteTasks(n)` with open tasks, else `cancelNote`. */
  note: string | null;
  childNames: string[];
  /** No member column on review_items yet; Step 2 adds the picker. */
  memberIds: string[];
  /** The source email (the item's newest; Edit Details lists its events). */
  emailId: string;
  /**
   * Emails behind this item, newest first: the item's own email, then the
   * target's when a change/cancel's target came from a different email
   * (AC17).
   */
  sourceEmailIds: string[];
  /**
   * The card's source line. An upload (YAR-556) reads its own subject
   * ('Photo · Oct 26') over "Added by you", with its kind's icon.
   */
  source: { subject: string; sender: string; upload?: UploadKind | null };
  /** Linked new tasks of a new or change event card (related_event_id → this item's id). */
  tasks: ReviewListItem[];
  actionLabel: string;
  /** The review item as read (production TaskCard renders nested tasks from it). */
  row: ReviewItem;
  /**
   * v1.0 badge on a top-level task: the scheduled event it joins, by its
   * title on the schedule. Nested tasks draw it from their parent card.
   */
  eventBadge: string | null;
  /** The update item the badge names; null when that item is off the list. */
  eventBadgeItemId: string | null;
  /**
   * The zero-diff update item this new task's badge names when that item
   * renders no card (E-7): Confirm all sends it with the task; the task's
   * Don't add takes it along when it was the last one (F-11).
   */
  silentAnchorId: string | null;
  /** A cancellation's open tasks, cancelled on confirm (E3). Never confirmed, never counted. */
  cancelTasks: CancelTaskLine[];
}

/** An open task of a cancelled event (tasks table), as the service reads it. */
export interface OpenScheduleTask {
  id: string;
  title: string;
  due: string | null;
  isTimeUnknown: boolean;
}

export interface CancelTaskLine {
  id: string;
  title: string;
  whenLabel: string | null;
}

export type ConfirmGate =
  | { kind: 'ready' }
  | { kind: 'blocked-events'; count: number }
  | { kind: 'needs-task-confirmation'; tasks: ReviewListItem[] };

export interface BuildReviewListOptions {
  /** The user's profile timezone; null falls back like every formatter. */
  timezone: string | null;
  /**
   * The household's children, for the children line of a change diff.
   * Optional: without it a change card simply lists no children change.
   */
  children?: readonly Child[];
  /** The targets could not be read: hinted items render as new. */
  targetsFailed?: boolean;
  /** Open tasks by cancelled event id (E3). Absent → no tasks on cancel cards. */
  openTasksByEvent?: ReadonlyMap<string, readonly OpenScheduleTask[]>;
  /** Local removals (C5): a hidden card leaves at once, before the refetch. */
  hidden?: ReadonlySet<string>;
}

/** Where focus goes when the last card leaves: the page heading. */
export const HEADING_FOCUS = '__heading__';

/** Row title when the email has no subject (same as today's Review). */
export const NO_SUBJECT = '(no subject)';

const ACTION_LABEL: Record<ReviewItemKind, string> = {
  new: REVIEW_COPY.actionDontAdd,
  change: REVIEW_COPY.actionDontChange,
  cancelled: REVIEW_COPY.actionKeepEvent,
};

/**
 * Sender as the cards show it: the ingestion label, else the From display
 * name; '' when only an address is known.
 */
export function senderNameOf(email: {
  from_email: string;
  sender_label: string | null;
}): string {
  const label = email.sender_label?.trim();
  const name =
    label && !label.includes('@') ? label : senderDisplayName(email.from_email);
  return name.includes('@') ? '' : name;
}

function sourceOf(item: ReviewListSourceItem): ReviewListItem['source'] {
  const subject = item.email.subject?.trim() || NO_SUBJECT;
  if (item.email.source === 'upload') {
    // Never the uploader's address; a missing kind still reads as a photo.
    return {
      subject,
      sender: REVIEW_COPY.sourceAddedBy,
      upload: item.email.uploadKind ?? 'photo',
    };
  }
  return { subject, sender: senderNameOf(item.email), upload: null };
}

/** "Tue, Oct 28 · 5:00–6:00 PM"; the date alone when the time is unknown. */
function whenOf(
  start: string | null,
  end: string | null,
  timeUnknown: boolean,
  timezone: string | null,
  isEvent: boolean,
): string | null {
  if (!start) return null;
  const day = formatLongDateRange(start, end, timezone);
  if (timeUnknown) return day;
  const time = isEvent
    ? formatTimeRange(start, end, timezone)
    : formatTime(start, timezone);
  return `${day} · ${time}`;
}

function childNamesOf(item: ReviewItem): string[] {
  const names = (item.child_names ?? []).filter((n) => n.trim());
  if (names.length > 0) return names;
  return item.child_name?.trim() ? [item.child_name] : [];
}

/**
 * The badge of a new task whose related item is not a card on the list (F-7):
 * an update of an event whose target was read. Anything else — a cancel,
 * a task update, an unread target — has no badge.
 */
function offListBadge(
  item: ReviewListSourceItem,
  visibleIds: ReadonlySet<string>,
  targets: Map<string, ProposalTarget>,
): string | null {
  const related = item.related;
  if (item.type !== 'task' || !related || visibleIds.has(related.id)) {
    return null;
  }
  if (
    related.type !== 'event' ||
    !related.update_hint ||
    related.proposed_action !== 'update' ||
    !related.supersedes_event_id
  ) {
    return null;
  }
  const target = targets.get(related.supersedes_event_id);
  return target?.kind === 'event' ? target.title : null;
}

function toCard(
  item: ReviewListSourceItem,
  targets: Map<string, ProposalTarget>,
  opts: BuildReviewListOptions,
  visibleIds: ReadonlySet<string>,
): ReviewListItem {
  const view = resolveProposal(item, targets, opts.targetsFailed ?? false);
  const type: ReviewListItem['type'] = item.type === 'task' ? 'task' : 'event';
  const isEvent = type === 'event';
  const start = isEvent
    ? item.start_datetime
    : (item.due_datetime ?? item.start_datetime);

  let kind: ReviewItemKind = 'new';
  let title = item.title;
  let whenLabel = whenOf(
    start,
    isEvent ? item.end_datetime : null,
    item.is_time_unknown,
    opts.timezone,
    isEvent,
  );
  let placeLabel = item.location?.trim() || null;
  let detailLocation: string | null = null;
  const sourceEmailIds = [item.email_id];
  let changes: ProposalChange[] = [];
  let note: string | null = null;
  let cancelTasks: CancelTaskLine[] = [];

  if (view.kind === 'resolved') {
    const { target } = view;
    // The schedule item may have come from an earlier email (AC17).
    if (target.emailId && target.emailId !== item.email_id) {
      sourceEmailIds.push(target.emailId);
    }
    // The card names the item as it is on the schedule today.
    title = target.title;
    const targetWhen = whenOf(
      target.start,
      target.end,
      target.isTimeUnknown,
      opts.timezone,
      target.kind === 'event',
    );
    if (view.action === 'update') {
      kind = 'change';
      changes = diffProposal(
        item,
        target,
        [...(opts.children ?? [])],
        opts.timezone,
      );
      whenLabel = whenLabel ?? targetWhen;
      // The sheet shows where it is today; the diff carries any move (R4).
      detailLocation = target.location?.trim() || null;
      placeLabel = null; // never on a change card
    } else {
      kind = 'cancelled';
      if (target.kind === 'event') {
        cancelTasks = (opts.openTasksByEvent?.get(target.id) ?? []).map(
          (t) => ({
            id: t.id,
            title: t.title,
            whenLabel: whenOf(
              t.due,
              null,
              t.isTimeUnknown,
              opts.timezone,
              false,
            ),
          }),
        );
      }
      note =
        cancelTasks.length > 0
          ? REVIEW_COPY.cancelNoteTasks(cancelTasks.length)
          : REVIEW_COPY.cancelNote;
      whenLabel = targetWhen;
      placeLabel = target.location?.trim() || placeLabel;
    }
  }

  // Change and cancel cards never need a date (controller ruling): an update
  // keeps an unmentioned date, a cancel takes the target's.
  const isNew = kind === 'new';
  return {
    id: item.id,
    type,
    kind,
    title,
    whenLabel,
    // An unverified cancel (targets unreadable) is still a cancel: no date.
    undatedEvent:
      isNew &&
      isEvent &&
      !item.start_datetime &&
      // Only a hinted row is a cancel on the server; a bare 'cancel' is
      // inserted as a normal event and needs a date like any other.
      !(item.update_hint && item.proposed_action === 'cancel'),
    undatedTask: isNew && !isEvent && !item.due_datetime,
    placeLabel,
    detailLocation: kind === 'change' ? detailLocation : placeLabel,
    changes,
    note,
    childNames: childNamesOf(item),
    memberIds: [],
    emailId: item.email_id,
    sourceEmailIds,
    source: sourceOf(item),
    tasks: [],
    actionLabel: ACTION_LABEL[kind],
    row: item,
    // Only a new task is badged, as on the list (a task change names itself).
    eventBadge: isNew ? offListBadge(item, visibleIds, targets) : null,
    eventBadgeItemId: null,
    silentAnchorId: null,
    cancelTasks,
  };
}

const needsALook = (card: ReviewListItem) => card.kind !== 'new';

/** A folded task as a top-level card, badged with the event card it left. */
function promoted(
  task: ReviewListItem,
  from: ReviewListItem,
  silent: boolean,
): ReviewListItem {
  return {
    ...task,
    eventBadge: from.title,
    eventBadgeItemId: from.id,
    silentAnchorId: silent ? from.id : null,
  };
}

/**
 * One list: top-level cards whose kind !== 'new' first (read order kept),
 * then new items by created_at; see the module comment for the five steps.
 */
export function buildReviewList(
  items: readonly ReviewListSourceItem[],
  targets: ReadonlyMap<string, ProposalTarget>,
  opts: BuildReviewListOptions,
): ReviewListItem[] {
  const targetMap = new Map(targets);
  const failed = opts.targetsFailed ?? false;
  const visible = items.filter(
    (i) =>
      i.status !== 'dismissed' &&
      i.status !== 'committed' &&
      // An orphan cancel (target gone) does nothing on confirm — the server
      // skips it (cancels_skipped) — so it is not a card at all. A task
      // linked to it becomes a top-level card.
      resolveProposal(i, targetMap, failed).kind !== 'gone',
  );
  const visibleIds = new Set(visible.map((i) => i.id));
  const cards = new Map(
    visible.map((i) => [i.id, toCard(i, targetMap, opts, visibleIds)] as const),
  );

  // 2. Fold.
  const folded: ReviewListItem[] = [];
  for (const item of visible) {
    const card = cards.get(item.id);
    if (!card) continue;
    const linked =
      card.type === 'task' && item.related_event_id
        ? cards.get(item.related_event_id)
        : undefined;
    const parent = linked?.type === 'event' ? linked : undefined;
    if (
      parent &&
      card.kind === 'new' &&
      (parent.kind === 'new' || parent.kind === 'change')
    ) {
      parent.tasks.push(card);
      continue;
    }
    folded.push(card);
  }

  // 3. Silence — before hides, so a hidden task never un-silences its anchor.
  const silenced = folded.flatMap((card) =>
    card.kind === 'change' && card.changes.length === 0 && card.tasks.length > 0
      ? card.tasks.map((t) => promoted(t, card, true))
      : [card],
  );

  // 4. Hide.
  const hidden = opts.hidden ?? new Set<string>();
  const top = silenced.flatMap((card): ReviewListItem[] => {
    const tasks = card.tasks.filter((t) => !hidden.has(t.id));
    if (!hidden.has(card.id)) return [{ ...card, tasks }];
    return card.kind === 'change'
      ? tasks.map((t) => promoted(t, card, false))
      : [];
  });

  // 5. Order.
  const readAt = new Map(items.map((i, n) => [i.id, n] as const));
  const created = (c: ReviewListItem) => Date.parse(c.row.created_at);
  const fresh = top
    .filter((c) => !needsALook(c))
    .sort(
      (a, b) =>
        created(a) - created(b) ||
        (readAt.get(a.id) ?? 0) - (readAt.get(b.id) ?? 0),
    );
  return [...top.filter(needsALook), ...fresh];
}

function allCards(items: readonly ReviewListItem[]): ReviewListItem[] {
  return items.flatMap((c) => [c, ...c.tasks]);
}

/** Every card id on the list, folded tasks included (never a silent anchor). */
export function listedIds(items: readonly ReviewListItem[]): Set<string> {
  return new Set(allCards(items).map((c) => c.id));
}

/** What Confirm all sends: listedIds plus each listed card's silentAnchorId, deduped, list order. */
export function confirmItemIds(items: readonly ReviewListItem[]): string[] {
  const ids = new Set<string>();
  for (const card of allCards(items)) {
    ids.add(card.id);
    if (card.silentAnchorId) ids.add(card.silentAnchorId);
  }
  return [...ids];
}

/** Every card, folded tasks included. */
export function countItems(items: readonly ReviewListItem[]): number {
  return allCards(items).length;
}

/**
 * Today's Confirm gate (EmailReviewModal → `reviewConfirmGate`): undated
 * events block outright, then undated tasks ask "Confirm without a date?".
 */
export function confirmGateFor(items: readonly ReviewListItem[]): ConfirmGate {
  const cards = allCards(items);
  const events = cards.filter((c) => c.undatedEvent).length;
  if (events > 0) return { kind: 'blocked-events', count: events };
  const tasks = cards.filter((c) => c.undatedTask);
  if (tasks.length > 0) return { kind: 'needs-task-confirmation', tasks };
  return { kind: 'ready' };
}

/**
 * Where focus goes when `id` leaves the list (E-2): a card goes to the next
 * card, else the previous, else the heading; a nested task to the next task
 * under the same event, else the previous, else its event card.
 */
export function focusAfterRemoval(
  items: readonly ReviewListItem[],
  id: string,
): string {
  const at = items.findIndex((c) => c.id === id);
  if (at >= 0) {
    return items[at + 1]?.id ?? items[at - 1]?.id ?? HEADING_FOCUS;
  }
  for (const parent of items) {
    const i = parent.tasks.findIndex((t) => t.id === id);
    if (i >= 0) {
      return parent.tasks[i + 1]?.id ?? parent.tasks[i - 1]?.id ?? parent.id;
    }
  }
  return HEADING_FOCUS;
}
